/* ============================================================================================== */

import produce from 'immer';
import api from '../../services/api';
import AmplitudeTools from '../../utils/AmplitudeTools';

export const AuthActions = {
  RESET_ERRORS: 'auth/RESET_ERRORS',

  // LogIn
  LOGIN_PENDING: 'auth/LOGIN_PENDING',
  LOGIN_SUCCEEDED: 'auth/LOGIN_SUCCEEDED',
  LOGIN_FAILED: 'auth/LOGIN_FAILED',

  // SignUp
  SIGNUP_PENDING: 'auth/SIGNUP_PENDING',
  SIGNUP_SUCCEEDED: 'auth/SIGNUP_SUCCEEDED',
  SIGNUP_FAILED: 'auth/SIGNUP_FAILED',

  // Welcome Set Password
  SET_PASSWORD_PENDING: 'auth/SET_PASSWORD_PENDING',
  SET_PASSWORD_SUCCEEDED: 'auth/SET_PASSWORD_SUCCEEDED',
  SET_PASSWORD_FAILED: 'auth/SET_PASSWORD_FAILED',

  // Logout
  LOGOUT: 'auth/LOGOUT',
};

export const resetErrors = () => {
  return async (dispatch) => {
    dispatch({ type: AuthActions.RESET_ERRORS });
  };
};

// Refreshes the currently logged-in user's session using the httpOnly refresh
// cookie (sent automatically). The API sets a fresh access cookie via Set-Cookie
// on the response — nothing is stored client-side. Not used for manual form logins.
export function refreshSession() {
  return async (dispatch) => {
    dispatch({ type: AuthActions.LOGIN_PENDING });
    try {
      const response = await api.post(`/v1/auth/token`, {});

      dispatch({
        type: AuthActions.LOGIN_SUCCEEDED,
        payload: { data: response.data },
      });
    } catch (err) {
      dispatch({
        type: AuthActions.LOGIN_FAILED,
        errors: err,
      });
    }
  };
}

// pass true to redirect back to the current page after logging back in
export function logout(fromUserAction, redirectBack) {
  return async (dispatch) => {
    // The auth cookies are httpOnly, so only the server can clear them.
    try {
      await api.delete(`/v1/auth/logout`);
    } catch (error) {
      console.warn('Logout request failed', error);
    }

    AmplitudeTools.handleUserLogout(fromUserAction);

    // Using 'app/RESET' to have logout as an app cleaner. If there is collateral effects, could use 'AuthActions.LOGOUT'
    dispatch({ type: 'app/RESET' });
    if (redirectBack) {
      const refPath = window.location.href; // pass full URL since login is in web project on main kithward domain
      window.location.assign(
        `${process.env.REACT_APP_CONSUMER}/auth/login?return_to=${encodeURIComponent(
          refPath,
        )}`,
      );
    } else {
      window.location.assign(`${process.env.REACT_APP_CONSUMER}/auth/login`);
    }
  };
}

const INITIAL_STATE = {
  loading: false,
  isAuthenticated: false,
  user: null,
  errors: [],
};

const authReducer = produce((draft, action) => {
  switch (action.type) {
    case 'app/RESET':
      return INITIAL_STATE;

    case AuthActions.RESET_ERRORS:
      draft.errors = [];
      draft.loading = false;
      break;

    // LOGIN
    case AuthActions.LOGIN_PENDING:
      draft.loading = true;
      break;

    case AuthActions.LOGIN_SUCCEEDED:
      const data = action.payload.data;
      draft.loading = false;
      draft.isAuthenticated = true;
      draft.user = data;
      draft.errors = [];
      break;

    case AuthActions.LOGIN_FAILED:
      draft.loading = false;
      draft.isSaving = false;
      draft.isAuthenticated = false;
      draft.errors = action.errors;
      break;

    // Sign Up
    case AuthActions.SIGNUP_PENDING:
      draft.loading = true;
      break;

    case AuthActions.SIGNUP_SUCCEEDED:
      draft.errors = [];
      draft.loading = false;
      break;

    case AuthActions.SIGNUP_FAILED:
      draft.errors = action.errors;
      draft.loading = false;
      break;

    // Set Password
    case AuthActions.SET_PASSWORD_PENDING:
      draft.loading = true;
      break;

    case AuthActions.SET_PASSWORD_SUCCEEDED:
      draft.errors = [];
      draft.loading = false;
      break;

    case AuthActions.SET_PASSWORD_FAILED:
      draft.errors = action.errors;
      draft.loading = false;
      break;

    default:
      break;
  }
}, INITIAL_STATE);

export default authReducer;
